import { ChangeDetectionStrategy, Component, Inject, Injector, OnDestroy } from '@angular/core';
import { IExtendedSearchSettings } from '../extended-search-types.service';
import { MatDialogRef } from '@angular/material/dialog';
import {
    OPERATION_OVERLAY_LARGE_CONTENT_DATA,
    OperationOverlayLargeDialogComponent,
} from '@fi-app/utilities/operation-overlay-large/operation-overlay-large-dialog/operation-overlay-large-dialog.component';
import { LIST_CONTAINER_DATA, ListData } from '@fi-app/components/items-list/list-data.service';
import { ExtendedSearchListService } from '../extended-search-list.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ItemsListElementType } from '@fi-app/components/items-list/items-list-header';
import { OperationOverlayLargeNg4Service } from '@fi-app/utilities/operation-overlay-large/operation-overlay-large.service';
import { promiseUnwrap } from '@fi-shared/fi-shared-services/extensions.service';
import { isDefined } from '@fi-shared/utils/is-defined';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-extended-search-list',
    styleUrls: ['./extended-search-list.component.scss'],
    template: `<ng-container *ngComponentOutlet="contentComponent; injector: contentInjector"></ng-container>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ExtendedSearchListComponent implements OnDestroy {
    contentComponent: any;
    contentInjector: Injector;

    private ListData: ListData<any>;
    private viewSettings;

    constructor(
        @Inject(OPERATION_OVERLAY_LARGE_CONTENT_DATA) data: IExtendedSearchSettings,
        private dialogRef: MatDialogRef<OperationOverlayLargeDialogComponent>,
        private injector: Injector,
        private extendedSearchListService: ExtendedSearchListService,
        private operationOverlayLargeService: OperationOverlayLargeNg4Service,
    ) {
        const listSettings = data.listSettings,
            params = dialogRef.componentInstance.params;
        this.viewSettings = data.viewSettings;

        if (listSettings.listModel) {
            this.ListData = listSettings.listModel();
            this.ListData.MultiSelect = isDefined(params.multiple) ? params.multiple : false;

            this.ListData.onInitialized.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                const afterLoadList = params.afterListLoad;
                if (afterLoadList) afterLoadList(this.ListData);
            });

            const self = this;
            if (data.filterPrams) {
                this.ListData.getFilterParams = (function (getFilterParamsFunc) {
                    return () => {
                        return Object.assign(getFilterParamsFunc.call(self.ListData), data.filterPrams);
                    };
                })(this.ListData.getFilterParams);
            }

            const savedSort = extendedSearchListService.getSavedSort(this.ListData.ObjectType);
            if (savedSort) this.ListData.Sort = Object.assign({}, savedSort);

            const savedFilter = extendedSearchListService.getSavedFilter(this.ListData.ObjectType);
            this.ListData.FilterType = savedFilter ? Number(savedFilter) : SideListOptionType.SharedWithMe;
            //this.listData.Selectable = true; todo not used?
            this.ListData.Statefull = false;

            if (this.ListData.ObjectType !== ObjectTypes.CheckList && this.ListData.ObjectType !== ObjectTypes.Form) {
                this.ListData.Header[0].Properties.ElementType = ItemsListElementType.ImgIcon;
                this.ListData.Header[1].Properties.ElementType = ItemsListElementType.SpanPlain;
            }

            for (let i = 0; i < this.ListData.Header.length; i++) {
                if (this.ListData.Header[i].Properties.ElementType == ItemsListElementType.ArrayA)
                    this.ListData.Header[i].Properties.ElementType = ItemsListElementType.ArraySpan;

                if (this.ListData.Header[i].Properties.ElementType == ItemsListElementType.A)
                    this.ListData.Header[i].Properties.ElementType = ItemsListElementType.SpanPlain;
            }

            this.ListData.GridItemClick = (item) => {
                this.openView(item);
            };

            this.ListData.CcmListItemNameClick = ($event, item) => {
                this.openView(item);
            };
        }

        this.contentComponent = data.listSettings.component;
        this.contentInjector = Injector.create({
            providers: [
                {
                    provide: LIST_CONTAINER_DATA,
                    useValue: Object.assign({ ListData: this.ListData }, data.listSettings.resolve),
                },
            ],
            parent: injector,
        });
    }

    private isViewOpening = false;
    private openView(viewModel) {
        if (this.isViewOpening) return;

        this.isViewOpening = true;
        setTimeout(() => {
            viewModel.Checked = true;
            this.isViewOpening = false;
        });
        if (this.viewSettings) {
            promiseUnwrap(this.viewSettings.resolve(viewModel)).then((resolvedData) => {
                this.operationOverlayLargeService
                    .openOperationOverlayLargeDialog(this.viewSettings.component, resolvedData, {
                        SelectedListItem: viewModel,
                    })
                    .afterClosed()
                    .subscribe((viewModel) => {
                        if (isDefined(viewModel)) this.dialogRef.close(viewModel);
                    });
            });
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        if (this.ListData) {
            this.extendedSearchListService.setSavedSort(this.ListData.ObjectType, this.ListData.Sort);
            this.extendedSearchListService.setSavedFilter(this.ListData.ObjectType, this.ListData.FilterType);
        }
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
